import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, X, Check, Eye, EyeOff } from 'lucide-react';
import { get, post, put, del } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Skeleton } from '@/components/atoms/Skeleton';
import { TickerAutocomplete } from '@/components/molecules/TickerAutocomplete';
import { toast } from '@/store/toastStore';
import { confirmModal } from '@/store/confirmStore';

/**
 * 어드민 — 종목 상세 커스텀 뱃지 관리 (content 권한 이상).
 *  - 종목코드 + 텍스트 + 배경/텍스트색으로 뱃지 등록
 *  - 목록 테이블: 종목코드/종목명/미리보기/색상/노출여부/순서 + 추가·수정·삭제·노출토글
 *  - 표시는 종목 상세(TickerPage) 상단. 한 종목에 여러 개 가능.
 */

interface BadgeRow {
  id: number;
  ticker_code: string;
  ticker_name: string | null;
  label: string;
  color: string;
  text_color: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/** 뱃지 미리보기 칩 */
function BadgeChip({ label, color, textColor }: { label: string; color: string; textColor: string }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold"
      style={{ backgroundColor: color, color: textColor }}
    >
      {label || '미리보기'}
    </span>
  );
}

/** 색상 입력 (color picker + hex 텍스트) */
function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-[var(--color-text-sub)]">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={HEX.test(value) ? value : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-10 cursor-pointer rounded border border-[var(--color-border)] bg-white p-0.5"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#ef4444"
          className="h-9 w-28 rounded-lg border border-[var(--color-border)] bg-white px-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
        />
      </div>
    </div>
  );
}

const EMPTY_DRAFT = {
  tickerCode: '',
  tickerName: '',
  label: '',
  color: '#ef4444',
  textColor: '#ffffff',
  sortOrder: '100',
};

export function TickerBadgesPage() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'ticker-badges'],
    queryFn: () => get<{ rows: BadgeRow[] }>('/admin/ticker-badges'),
  });
  const rows = data?.rows ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'ticker-badges'] });

  // ── 신규 등록 ──────────────────────────────────────
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });

  const createMut = useMutation({
    mutationFn: () =>
      post('/admin/ticker-badges', {
        tickerCode: draft.tickerCode.trim(),
        label: draft.label.trim(),
        color: draft.color.trim(),
        textColor: draft.textColor.trim(),
        sortOrder: Number(draft.sortOrder) || 100,
      }),
    onSuccess: () => {
      toast.success('뱃지를 추가했어요.');
      setDraft({ ...EMPTY_DRAFT });
      invalidate();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? '추가에 실패했어요.'),
  });

  const handleCreate = (e: FormEvent) => {
    e.preventDefault();
    if (!draft.tickerCode.trim()) return toast.error('종목코드를 입력해주세요.');
    if (!draft.label.trim()) return toast.error('뱃지 텍스트를 입력해주세요.');
    if (!HEX.test(draft.color.trim())) return toast.error('배경색은 #rrggbb 형식이어야 해요.');
    if (!HEX.test(draft.textColor.trim())) return toast.error('텍스트색은 #rrggbb 형식이어야 해요.');
    createMut.mutate();
  };

  // ── 수정 ──────────────────────────────────────────
  const [editId, setEditId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState({ label: '', color: '', textColor: '', sortOrder: '100' });

  const startEdit = (r: BadgeRow) => {
    setEditId(r.id);
    setEditDraft({
      label: r.label,
      color: r.color,
      textColor: r.text_color,
      sortOrder: String(r.sort_order),
    });
  };

  const updateMut = useMutation({
    mutationFn: (vars: { id: number; body: Record<string, unknown> }) =>
      put(`/admin/ticker-badges/${vars.id}`, vars.body),
    onSuccess: () => {
      toast.success('수정했어요.');
      setEditId(null);
      invalidate();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? '수정에 실패했어요.'),
  });

  const saveEdit = (id: number) => {
    if (!editDraft.label.trim()) return toast.error('뱃지 텍스트를 입력해주세요.');
    if (!HEX.test(editDraft.color.trim())) return toast.error('배경색은 #rrggbb 형식이어야 해요.');
    if (!HEX.test(editDraft.textColor.trim())) return toast.error('텍스트색은 #rrggbb 형식이어야 해요.');
    updateMut.mutate({
      id,
      body: {
        label: editDraft.label.trim(),
        color: editDraft.color.trim(),
        textColor: editDraft.textColor.trim(),
        sortOrder: Number(editDraft.sortOrder) || 100,
      },
    });
  };

  // ── 노출 토글 / 삭제 ───────────────────────────────
  const toggleMut = useMutation({
    mutationFn: (vars: { id: number; isActive: boolean }) =>
      put(`/admin/ticker-badges/${vars.id}`, { isActive: vars.isActive }),
    onSuccess: invalidate,
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? '변경에 실패했어요.'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => del(`/admin/ticker-badges/${id}`),
    onSuccess: () => {
      toast.success('삭제했어요.');
      invalidate();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? '삭제에 실패했어요.'),
  });

  const handleDelete = async (r: BadgeRow) => {
    const ok = await confirmModal({
      title: '뱃지 삭제',
      message: `${r.ticker_name ?? r.ticker_code} · "${r.label}" 뱃지를 삭제할까요?`,
      tone: 'danger',
    });
    if (ok) deleteMut.mutate(r.id);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold">종목 커스텀 뱃지</h1>
        <p className="mt-1 text-sm text-[var(--color-text-sub)]">
          종목 상세 페이지 상단에 노출되는 뱃지를 관리해요. 한 종목에 여러 개 등록할 수 있어요.
        </p>
      </div>

      {/* ── 추가 폼 ───────────────────────────────── */}
      <form
        onSubmit={handleCreate}
        className="mb-6 rounded-2xl border border-[var(--color-border)] bg-white p-5"
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-64">
            <label className="mb-1 block text-xs font-medium text-[var(--color-text-sub)]">
              종목 검색
            </label>
            <TickerAutocomplete
              value={draft.tickerCode}
              displayLabel={
                draft.tickerCode
                  ? `${draft.tickerName || draft.tickerCode} (${draft.tickerCode})`
                  : undefined
              }
              onChange={(code, meta) =>
                setDraft((d) => ({ ...d, tickerCode: code, tickerName: meta?.name ?? '' }))
              }
              placeholder="종목명 또는 코드 검색"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--color-text-sub)]">
              뱃지 텍스트
            </label>
            <Input
              value={draft.label}
              onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
              placeholder="정치테마"
              maxLength={40}
              className="w-40"
            />
          </div>
          <ColorField
            label="배경색"
            value={draft.color}
            onChange={(v) => setDraft((d) => ({ ...d, color: v }))}
          />
          <ColorField
            label="텍스트색"
            value={draft.textColor}
            onChange={(v) => setDraft((d) => ({ ...d, textColor: v }))}
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--color-text-sub)]">
              순서
            </label>
            <Input
              type="number"
              value={draft.sortOrder}
              onChange={(e) => setDraft((d) => ({ ...d, sortOrder: e.target.value }))}
              className="w-20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--color-text-sub)]">
              미리보기
            </label>
            <div className="flex h-9 items-center">
              <BadgeChip label={draft.label} color={draft.color} textColor={draft.textColor} />
            </div>
          </div>
          <Button type="submit" loading={createMut.isPending}>
            <Plus className="h-4 w-4" /> 추가
          </Button>
        </div>
      </form>

      {/* ── 목록 테이블 ───────────────────────────── */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--color-border)] p-10 text-center text-sm text-[var(--color-text-sub)]">
          등록된 뱃지가 없어요.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[var(--color-border)] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left text-xs text-[var(--color-text-sub)]">
                <th className="p-3">종목코드</th>
                <th className="p-3">종목명</th>
                <th className="p-3">뱃지</th>
                <th className="p-3">색상</th>
                <th className="p-3 text-center">노출</th>
                <th className="p-3 text-center">순서</th>
                <th className="p-3 text-right">관리</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const editing = editId === r.id;
                return (
                  <tr key={r.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-3 font-mono">{r.ticker_code}</td>
                    <td className="p-3">{r.ticker_name ?? '-'}</td>
                    <td className="p-3">
                      {editing ? (
                        <Input
                          value={editDraft.label}
                          onChange={(e) => setEditDraft((d) => ({ ...d, label: e.target.value }))}
                          maxLength={40}
                          className="w-36"
                        />
                      ) : (
                        <BadgeChip label={r.label} color={r.color} textColor={r.text_color} />
                      )}
                    </td>
                    <td className="p-3">
                      {editing ? (
                        <div className="flex flex-col gap-2">
                          <ColorField
                            label="배경"
                            value={editDraft.color}
                            onChange={(v) => setEditDraft((d) => ({ ...d, color: v }))}
                          />
                          <ColorField
                            label="텍스트"
                            value={editDraft.textColor}
                            onChange={(v) => setEditDraft((d) => ({ ...d, textColor: v }))}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-xs text-[var(--color-text-sub)]">
                          <span
                            className="inline-block h-4 w-4 rounded border border-[var(--color-border)]"
                            style={{ backgroundColor: r.color }}
                          />
                          <span className="font-mono">{r.color}</span>
                          <span
                            className="ml-1 inline-block h-4 w-4 rounded border border-[var(--color-border)]"
                            style={{ backgroundColor: r.text_color }}
                          />
                          <span className="font-mono">{r.text_color}</span>
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <button
                        type="button"
                        title={r.is_active ? '노출 중 (클릭해 숨김)' : '숨김 (클릭해 노출)'}
                        onClick={() => toggleMut.mutate({ id: r.id, isActive: !r.is_active })}
                        className="inline-flex items-center justify-center"
                      >
                        {r.is_active ? (
                          <Eye className="h-5 w-5 text-[var(--color-primary)]" />
                        ) : (
                          <EyeOff className="h-5 w-5 text-[var(--color-text-sub)]" />
                        )}
                      </button>
                    </td>
                    <td className="p-3 text-center">
                      {editing ? (
                        <Input
                          type="number"
                          value={editDraft.sortOrder}
                          onChange={(e) => setEditDraft((d) => ({ ...d, sortOrder: e.target.value }))}
                          className="w-16"
                        />
                      ) : (
                        <span className="tabular-nums">{r.sort_order}</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1.5">
                        {editing ? (
                          <>
                            <Button
                              size="sm"
                              onClick={() => saveEdit(r.id)}
                              loading={updateMut.isPending}
                            >
                              <Check className="h-4 w-4" /> 저장
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditId(null)}>
                              <X className="h-4 w-4" /> 취소
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="secondary" onClick={() => startEdit(r)}>
                              <Pencil className="h-4 w-4" /> 수정
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(r)}
                              loading={deleteMut.isPending && deleteMut.variables === r.id}
                            >
                              <Trash2 className="h-4 w-4" /> 삭제
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
